import type { StreamEvent } from './streamReducer'
import type { PendingFunctionCall } from './types'

/**
 * Parses an SSE stream from the Claude Messages API into the same typed
 * `StreamEvent`s the reducer already consumes — the Anthropic sibling of
 * `sseParser.ts` (which handles the OpenAI Responses taxonomy). Because
 * both parsers emit the shared `StreamEvent` union, the reducer and the
 * render layer never learn which backend produced the stream.
 *
 * Anthropic event taxonomy (https://docs.claude.com/en/api/messages-streaming):
 *
 *     event: message_start          → thread.run.created (message id doubles
 *                                      as run id; the reducer stores it as
 *                                      threadId, which this backend ignores)
 *     event: content_block_start    → text: thread.message.created
 *                                      tool_use: …tool_call_started
 *     event: content_block_delta    → text_delta: thread.message.delta
 *                                      input_json_delta: …tool_call_args_delta
 *     event: content_block_stop     → text: thread.message.completed
 *     event: message_delta          → carries stop_reason (held, not emitted)
 *                                      and the final output_tokens
 *     event: message_stop           → stream.usage, then
 *                                      stop_reason "tool_use":
 *                                        thread.run.requires_tool_outputs
 *                                      otherwise: thread.run.completed
 *     event: error                  → stream.error
 *     event: ping                   → ignored
 *
 * Two shape differences from the Foundry parser worth naming:
 *
 * - **Single tool-call id.** Claude's `tool_use` blocks have one id
 *   (`toolu_…`) where the Responses API has two (`fc_…` output-item id +
 *   `call_…` call handle). We set `stepId === callId === toolu_…`; the
 *   reducer's id-rewrite on `requires_tool_outputs` becomes an identity op,
 *   which it already tolerates (the older-envelope Foundry path does the
 *   same).
 * - **Args accumulate here, not just in the reducer.** The pending-call
 *   list on `requires_tool_outputs` needs each call's full `argumentsJson`,
 *   so the parser accumulates `input_json_delta` fragments per block index
 *   in addition to yielding the deltas for live UI rendering.
 *
 * The frame-splitting loop is intentionally duplicated from `sseParser.ts`
 * (~30 lines) rather than extracted: the Foundry parser is validated and
 * heavily tested, and this one shouldn't force churn there. Unify into a
 * shared `sse.ts` if a third transport ever needs it.
 */
export async function* parseClaudeSseStream(
  body: ReadableStream<Uint8Array>,
): AsyncIterableIterator<StreamEvent> {
  const reader = body.getReader()
  const decoder = new TextDecoder('utf-8')
  let buffer = ''
  const state = new ClaudeStreamState()

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })

      // SSE frames end with double-newline. Process every complete frame
      // and keep the remainder in the buffer.
      let frameEnd: number
      while ((frameEnd = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, frameEnd)
        buffer = buffer.slice(frameEnd + 2)
        const parsed = parseFrame(frame)
        if (parsed) yield* state.apply(parsed.eventName, parsed.data)
      }
    }
  } finally {
    reader.releaseLock()
  }
}

function parseFrame(frame: string): { eventName: string; data: unknown } | null {
  let eventName: string | null = null
  const dataLines: string[] = []

  for (const rawLine of frame.split('\n')) {
    const line = rawLine.trimEnd()
    if (!line || line.startsWith(':')) continue // empty or comment
    if (line.startsWith('event:')) {
      eventName = line.slice(6).trim()
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trim())
    }
  }

  if (!eventName || dataLines.length === 0) return null

  let data: unknown
  try {
    data = JSON.parse(dataLines.join('\n'))
  } catch {
    return null
  }

  return { eventName, data }
}

// ---------------------------------------------------------------------------
// Per-message stream state
//
// Unlike the Foundry parser (a pure event-name mapping), the Messages API
// addresses content blocks by INDEX, and the tool-dispatch decision lives on
// `stop_reason` — so translation needs memory across frames: index → block
// identity, accumulated tool args, and the held stop_reason.
// ---------------------------------------------------------------------------

type BlockInfo =
  | { type: 'text' }
  | { type: 'tool_use'; id: string; name: string; argsJson: string }
  | { type: 'other' }

class ClaudeStreamState {
  private messageId = ''
  private stopReason: string | null = null
  private textBubbleCreated = false
  private blocks = new Map<number, BlockInfo>()
  private usage = {
    inputTokens: 0,
    outputTokens: 0,
    cacheReadInputTokens: 0,
    cacheCreationInputTokens: 0,
  }
  /** Insertion-ordered tool blocks so requires_tool_outputs preserves call order. */
  // The semicolon is load-bearing: without it, ASI joins `[]` with the
  // `*apply` line below into a multiplication expression.
  private toolOrder: number[] = [];

  *apply(eventName: string, data: unknown): Generator<StreamEvent> {
    if (!isObject(data)) return

    switch (eventName) {
      case 'message_start': {
        const message = isObject(data.message) ? data.message : undefined
        const id = message?.id
        if (typeof id !== 'string') return
        this.messageId = id
        // Anthropic reports the input-side split (including the cache
        // read/write counts) on message_start and the final output count on
        // message_delta, so both are folded into one stream.usage emitted at
        // message_stop.
        if (isObject(message?.usage)) this.mergeUsage(message.usage)
        yield { kind: 'thread.run.created', runId: id }
        return
      }

      case 'content_block_start': {
        const index = data.index
        const block = data.content_block
        if (typeof index !== 'number' || !isObject(block)) return
        if (block.type === 'text') {
          this.blocks.set(index, { type: 'text' })
          // One text bubble per response — subsequent text blocks (rare,
          // e.g. text after tool_use) append to the same bubble.
          if (!this.textBubbleCreated && this.messageId) {
            this.textBubbleCreated = true
            yield { kind: 'thread.message.created', messageId: this.messageId }
          }
          return
        }
        if (block.type === 'tool_use') {
          const id = typeof block.id === 'string' ? block.id : ''
          const name = typeof block.name === 'string' ? block.name : ''
          if (!id || !name) return
          this.blocks.set(index, { type: 'tool_use', id, name, argsJson: '' })
          this.toolOrder.push(index)
          yield { kind: 'thread.run.step.tool_call_started', stepId: id, toolName: name }
          return
        }
        // thinking / redacted_thinking / anything future — tracked so the
        // index math stays right, never rendered.
        this.blocks.set(index, { type: 'other' })
        return
      }

      case 'content_block_delta': {
        const index = data.index
        const delta = data.delta
        if (typeof index !== 'number' || !isObject(delta)) return
        if (delta.type === 'text_delta' && typeof delta.text === 'string') {
          if (!this.messageId) return
          yield {
            kind: 'thread.message.delta',
            messageId: this.messageId,
            textDelta: delta.text,
          }
          return
        }
        if (delta.type === 'input_json_delta' && typeof delta.partial_json === 'string') {
          const block = this.blocks.get(index)
          if (!block || block.type !== 'tool_use') return
          block.argsJson += delta.partial_json
          yield {
            kind: 'thread.run.step.tool_call_args_delta',
            stepId: block.id,
            argsDelta: delta.partial_json,
          }
          return
        }
        return // thinking_delta etc.
      }

      case 'content_block_stop': {
        const index = data.index
        if (typeof index !== 'number') return
        const block = this.blocks.get(index)
        if (block?.type === 'text' && this.messageId) {
          yield { kind: 'thread.message.completed', messageId: this.messageId }
        }
        // tool_use blocks stay quiet here — mirroring the Foundry
        // function_call behaviour, the row remains in_progress until
        // requires_tool_outputs flips it to dispatching.
        return
      }

      case 'message_delta': {
        const delta = data.delta
        if (isObject(delta) && typeof delta.stop_reason === 'string') {
          this.stopReason = delta.stop_reason
        }
        if (isObject(data.usage)) this.mergeUsage(data.usage)
        return
      }

      case 'message_stop': {
        // Emitted before the terminal event so the accounting for this
        // request is recorded even when the next event kicks off a
        // tool-dispatch round.
        yield { kind: 'stream.usage', ...this.usage }
        const pending = this.pendingCalls()
        if (this.stopReason === 'tool_use' && pending.length > 0) {
          // Emitted INSTEAD of run.completed — same contract as the
          // Foundry parser's response.completed handling.
          yield {
            kind: 'thread.run.requires_tool_outputs',
            runId: this.messageId,
            toolCalls: pending,
          }
          return
        }
        yield { kind: 'thread.run.completed' }
        return
      }

      case 'error': {
        const err = data.error
        const message =
          (isObject(err) && typeof err.message === 'string' ? err.message : undefined) ??
          'Stream error'
        yield { kind: 'stream.error', message }
        return
      }

      default:
        return // ping and unknown events
    }
  }

  /**
   * Folds an Anthropic `usage` object into the running totals. Fields merge
   * rather than replace: the input-side counts arrive on `message_start` and
   * `output_tokens` on `message_delta`, so an absent field must leave the
   * previously-seen value alone rather than zero it.
   */
  private mergeUsage(usage: Record<string, unknown>): void {
    const num = (v: unknown): number | null => (typeof v === 'number' ? v : null)
    this.usage = {
      inputTokens: num(usage.input_tokens) ?? this.usage.inputTokens,
      outputTokens: num(usage.output_tokens) ?? this.usage.outputTokens,
      cacheReadInputTokens: num(usage.cache_read_input_tokens) ?? this.usage.cacheReadInputTokens,
      cacheCreationInputTokens:
        num(usage.cache_creation_input_tokens) ?? this.usage.cacheCreationInputTokens,
    }
  }

  private pendingCalls(): PendingFunctionCall[] {
    const out: PendingFunctionCall[] = []
    for (const index of this.toolOrder) {
      const block = this.blocks.get(index)
      if (!block || block.type !== 'tool_use') continue
      out.push({
        stepId: block.id,
        callId: block.id,
        name: block.name,
        argumentsJson: block.argsJson,
      })
    }
    return out
  }
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}
